import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import emptyIcon from '/emptyTable.svg';

import { formatSwissNumber } from 'utils/formatNumber';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import SynthesisTableColumns from './SynthesisTableColumns';

interface Props {
  isPDF?: boolean;
}

const pdfColumnWidth = {
  2: 'w-full flex-[0_0_20%]',
};

const SynthesisTable = ({ isPDF }: Props) => {
  const { crops } = useCroptimizer();

  const { t } = useTranslation();
  const getTotal = useMemo(() => {
    let area = 0;
    let total = 0;
    let cost = 0;
    let price = 0;

    if (crops) {
      crops.forEach((crop) => {
        area += Number(crop.area);
        total += Number(crop.productmt);
        cost += Number(crop.cost);
        price += Number(crop.cost) / Number(crop.productmt);
      });
    }

    return {
      area,
      total,
      cost,
      price,
    };
  }, [crops]);

  const getList = useMemo(() => {
    const result: string[] = [];
    crops?.forEach((crop) => {
      if (!result.includes(crop.productType)) {
        result.push(crop.productType);
      }
    });

    return result;
  }, [crops]);

  const getValues = (product: string) => {
    let area = 0;
    let cost = 0;
    let total = 0;
    crops.forEach((crop) => {
      if (crop.productType === product) {
        area += Number(crop.area);
        cost += Number(crop.cost);
        total += Number(crop.productmt);
      }
    });

    return {
      area,
      cost,
      total,
    };
  };

  return (
    <div className="w-full overflow-scroll">
      <div
        className={`${
          !isPDF ? 'w-full' : ''
        } mx-auto bg-[#FAFAFA] md:overflow-scroll`}
      >
        <SynthesisTableColumns isPDF={isPDF} pdfColumnWidth={pdfColumnWidth} />
        {crops.length > 0 ? (
          <>
            {getList?.map((product, i) => (
              <div
                key={i}
                className="flex px-4 pt-3 pb-3 border-b bg-white items-center w-full divide-x divide-transparent"
              >
                <div
                  className={`w-[350px] ${
                    isPDF ? pdfColumnWidth['2'] : ''
                  } flex px-2`}
                >
                  <div className="w-full text-center">{product}</div>
                </div>
                <div
                  className={`w-[185px] ${
                    isPDF ? pdfColumnWidth['2'] : ''
                  } text-center px-2`}
                >
                  {formatSwissNumber(getValues(product)?.area)}
                </div>
                <div
                  className={`w-[185px] ${
                    isPDF ? pdfColumnWidth['2'] : ''
                  } text-center px-2`}
                >
                  {formatSwissNumber(getValues(product)?.total)}
                </div>
                <div
                  className={`w-[185px] ${
                    isPDF ? pdfColumnWidth['2'] : ''
                  } text-center px-2`}
                >
                  {formatSwissNumber(
                    getValues(product)?.cost / getValues(product)?.total,
                  )}
                </div>
                <div
                  className={`w-[185px] ${
                    isPDF ? pdfColumnWidth['2'] : ''
                  } text-center px-2`}
                >
                  {formatSwissNumber(getValues(product)?.cost)}
                </div>
              </div>
            ))}

            <div className="flex px-4 pb-4 mt-4 divide-x divide-transparent">
              <div
                className={`w-[350px] ${
                  isPDF ? pdfColumnWidth['2'] : ''
                } font-bold text-center px-2`}
              >
                {t('total')}
              </div>
              <div
                className={`w-[185px] ${
                  isPDF ? pdfColumnWidth['2'] : ''
                } font-bold text-center px-2`}
              >
                {formatSwissNumber(getTotal?.area)}
              </div>
              <div
                className={`w-[185px] ${
                  isPDF ? pdfColumnWidth['2'] : ''
                } font-bold text-center px-2`}
              >
                {formatSwissNumber(getTotal?.total)}
              </div>
              <div
                className={`w-[185px] ${
                  isPDF ? pdfColumnWidth['2'] : ''
                } font-bold text-center px-2`}
              >
                {/* {maskValueHelper(Number(getTotal?.price.toFixed(0)))
                  ?.split('.')?.[0]
                  .replace('.', ',')} */}
              </div>
              <div
                className={`w-[185px] ${
                  isPDF ? pdfColumnWidth['2'] : ''
                } font-bold text-center px-2`}
              >
                {formatSwissNumber(getTotal?.cost)}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white w-full">
            <div className="h-[250px] flex justify-center items-center flex-col">
              <div>
                <img src={emptyIcon} alt="" />
              </div>
              <div className="mt-6 font-medium text-secondaryText">
                {t('synthesisTableEmpty')}
              </div>
            </div>

            <div className="flex pl-6 py-4 mt-4">
              <div className="w-[160px] ml-4 font-bold">{t('total')}</div>
              <div className="min-w-[185px] font-bold text-center">-</div>
              <div className="min-w-[195px] font-bold text-center">-</div>
              <div className="min-w-[190px] font-bold text-center">-</div>
              <div className="min-w-[185px] font-bold text-center">-</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SynthesisTable;
